/**
 * Locale registry — single source of truth for every locale the site
 * supports. Every SEO artifact (hreflang tags, sitemap generation,
 * locale switcher, canonical management, translation pipeline) reads
 * from this file. Adding a new locale means adding one row here —
 * everything downstream picks it up automatically.
 *
 * `code` is the ISO 639-1 language + ISO 3166 country combination used
 * in URLs and hreflang tags (e.g. `en-us`). Lowercased because URLs are
 * case-insensitive per RFC 3986 and lowercase is convention.
 *
 * `hreflang` is the canonical hreflang value (mixed case per BCP 47).
 * Google normalizes case but publishing the canonical form avoids
 * validator warnings.
 *
 * `dir` is text direction — for RTL locales (ar-*, he-*, ur-*, fa-*)
 * we'd set 'rtl'. Currently we don't ship any RTL locales but the field
 * is here so adding one is a one-line change.
 *
 * `contentPriority` drives translation-pipeline ordering. Locales with
 * higher priority (their pharma market size relative to our current
 * revenue mix) get translated first. Cost-per-translation is the same
 * regardless of priority; this only affects the ORDER, not the total
 * spend.
 */

export interface Locale {
  code: string;
  hreflang: string;
  language: string;
  region: string;
  name: string;
  nativeName: string;
  currency: string;
  dir: 'ltr' | 'rtl';
  contentPriority: number;
  isDefault?: boolean;
}

/* Order is display order in the language switcher.
   isDefault: true → this locale is served at `/` (no prefix).
   Only ONE locale can be default. */
export const LOCALES: Locale[] = [
  { code: 'en-us', hreflang: 'en-US', language: 'English',    region: 'United States',  name: 'English (US)',        nativeName: 'English (US)',        currency: 'USD', dir: 'ltr', contentPriority: 1, isDefault: true },
  { code: 'en-gb', hreflang: 'en-GB', language: 'English',    region: 'United Kingdom', name: 'English (UK)',        nativeName: 'English (UK)',        currency: 'GBP', dir: 'ltr', contentPriority: 2 },
  { code: 'en-in', hreflang: 'en-IN', language: 'English',    region: 'India',          name: 'English (India)',     nativeName: 'English (India)',     currency: 'INR', dir: 'ltr', contentPriority: 3 },
  { code: 'en-au', hreflang: 'en-AU', language: 'English',    region: 'Australia',      name: 'English (Australia)', nativeName: 'English (Australia)', currency: 'AUD', dir: 'ltr', contentPriority: 8 },
  { code: 'en-ca', hreflang: 'en-CA', language: 'English',    region: 'Canada',         name: 'English (Canada)',    nativeName: 'English (Canada)',    currency: 'CAD', dir: 'ltr', contentPriority: 9 },
  { code: 'en-nz', hreflang: 'en-NZ', language: 'English',    region: 'New Zealand',    name: 'English (NZ)',        nativeName: 'English (NZ)',        currency: 'NZD', dir: 'ltr', contentPriority: 15 },

  { code: 'de-de', hreflang: 'de-DE', language: 'German',     region: 'Germany',        name: 'German',              nativeName: 'Deutsch',             currency: 'EUR', dir: 'ltr', contentPriority: 4 },
  { code: 'fr-fr', hreflang: 'fr-FR', language: 'French',     region: 'France',         name: 'French',              nativeName: 'Français',            currency: 'EUR', dir: 'ltr', contentPriority: 5 },
  { code: 'es-es', hreflang: 'es-ES', language: 'Spanish',    region: 'Spain',          name: 'Spanish (Spain)',     nativeName: 'Español (España)',    currency: 'EUR', dir: 'ltr', contentPriority: 6 },
  { code: 'it-it', hreflang: 'it-IT', language: 'Italian',    region: 'Italy',          name: 'Italian',             nativeName: 'Italiano',            currency: 'EUR', dir: 'ltr', contentPriority: 10 },
  { code: 'nl-nl', hreflang: 'nl-NL', language: 'Dutch',      region: 'Netherlands',    name: 'Dutch',               nativeName: 'Nederlands',          currency: 'EUR', dir: 'ltr', contentPriority: 12 },

  { code: 'pt-br', hreflang: 'pt-BR', language: 'Portuguese', region: 'Brazil',         name: 'Portuguese (Brazil)', nativeName: 'Português (Brasil)',  currency: 'BRL', dir: 'ltr', contentPriority: 7 },
  { code: 'es-mx', hreflang: 'es-MX', language: 'Spanish',    region: 'Mexico',         name: 'Spanish (Mexico)',    nativeName: 'Español (México)',    currency: 'MXN', dir: 'ltr', contentPriority: 11 },
  { code: 'fr-ca', hreflang: 'fr-CA', language: 'French',     region: 'Canada',         name: 'French (Canada)',     nativeName: 'Français (Canada)',   currency: 'CAD', dir: 'ltr', contentPriority: 14 },

  { code: 'ja-jp', hreflang: 'ja-JP', language: 'Japanese',   region: 'Japan',          name: 'Japanese',            nativeName: '日本語',                currency: 'JPY', dir: 'ltr', contentPriority: 13 },
  { code: 'ko-kr', hreflang: 'ko-KR', language: 'Korean',     region: 'South Korea',    name: 'Korean',              nativeName: '한국어',                currency: 'KRW', dir: 'ltr', contentPriority: 16 },
];

export const DEFAULT_LOCALE = LOCALES.find(l => l.isDefault) ?? LOCALES[0];
export const LOCALE_CODES = LOCALES.map(l => l.code);

/* URL helpers — every route in the app uses these so URL construction
   stays consistent. Default locale serves at `/` (no prefix) so existing
   URLs keep working; every other locale gets the `/{code}` prefix. */
export function localizedPath(path: string, locale?: string): string {
  const l = locale ?? DEFAULT_LOCALE.code;
  const clean = path.startsWith('/') ? path : `/${path}`;
  if (l === DEFAULT_LOCALE.code) return clean;
  return `/${l}${clean === '/' ? '' : clean}`;
}

export function localeFromPath(pathname: string): string {
  /* Strip leading slash + take first segment. If it matches a known
     locale, use it; else fall back to default. */
  const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  if (first && LOCALE_CODES.includes(first)) return first;
  return DEFAULT_LOCALE.code;
}

export function stripLocalePrefix(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  if (first && LOCALE_CODES.includes(first)) {
    return pathname.replace(new RegExp(`^/${first}`), '') || '/';
  }
  return pathname;
}

export function getLocale(code: string): Locale | undefined {
  return LOCALES.find(l => l.code === code.toLowerCase());
}
